// common-utils.js

/**
 * This function takes a string as input and returns a new string
 * with the first character converted to uppercase.
 *
 * @param {string} string - The original string.
 * @returns {string} The modified string with the first character in uppercase.
 */
export function capitalizeFirstLetter(string) {
  return string?.charAt(0).toUpperCase() + string?.slice(1) || "";
}

//Check if an item is a string or a number and return a boolean
export function isStringOrNumber(value) {
  return typeof value === "string" || typeof value === "number";
}

//Drag an object
export function drag(event, obj) {
  event.stopPropagation();
  event.dataTransfer.setData("text", obj);
}

//Drop an object
export function drop(event, targetName, formValues) {
  try {
    // Prevent default behavior and event propagation
    event.preventDefault();
    event.stopPropagation();
    // Get the dropped element
    const dropped_element = JSON.parse(event.dataTransfer.getData("text"));
    // Check if formValues[targetName] exists
    if (formValues[targetName] !== undefined) {
      formValues[targetName] = dropped_element[0];
    } else {
      console.error("Data Undefined.");
    }
  } catch (error) {
    console.error("Error performing drop: ", error);
  }
}

/**
 * This function handles a drop event and identifies the data type of the second element
 * of the dropped data if it is an array.
 *
 * @param {Event} event - The drop event.
 * @returns {string} The data type of the second element of the dropped data if it is an array.
 *                   Possible return values are "text", "array", "object", "unknown",
 *                   "not an array or array does not have at least two elements".
 */
export function checkDrop(event) {
  const inputString = event.dataTransfer.getData("text");
  let item;
  try {
    // Try to parse the input string as JSON
    item = JSON.parse(inputString);
  } catch {
    // If it's not valid JSON, then it's a string
    return "text";
  }
  // Check if the object is an array
  if (Array.isArray(item) && item.length > 1) {
    // Get the second element of the array
    let secondElement = item[1];
    // Check the type of the second element
    if (typeof secondElement === "string") {
      return "text";
    } else if (Array.isArray(secondElement)) {
      return "array";
    } else if (typeof secondElement === "object") {
      return "object";
    } else {
      return "any";
    }
  } else {
    return "not an array or array does not have at least two elements";
  }
}

//Remove node occurrence within other nodes
export function removeNodeOccurrences(id, nodes) {
  nodes.value.map((oggetto) => {
    if (oggetto.userData && oggetto.userData.inputs) {
      if (Array.isArray(oggetto.userData.inputs)) {
        //If inputs is an array of objects, removes the object with the corresponding ID
        if (typeof oggetto.userData.inputs[0] === "object") {
          oggetto.userData.inputs = oggetto.userData.inputs.filter(
            (input) => input.id !== id,
          );
        }
        //If inputs is a string array, remove the string that matches the ID
        else {
          oggetto.userData.inputs = oggetto.userData.inputs.filter(
            (inputId) => inputId !== id,
          );
        }
      }
    }
    return oggetto;
  });
}

//Encode the string in UTF-8, then in base64
//Note: In accordance with the BE, a # symbol is added to the beginning of the encoded string.
export function encodeBase64(string) {
  return (
    "#" +
    btoa(
      encodeURIComponent(string).replace(
        /%([0-9A-F]{2})/g,
        function (match, p1) {
          return String.fromCharCode("0x" + p1);
        },
      ),
    )
  );
}

//Decode the string from base64, then from UTF-8
//Note: In accordance with the BE, the # symbol at the beginning of the decoded string is removed.
export function decodeBase64(base64) {
  return decodeURIComponent(
    atob(base64.slice(1))
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(""),
  );
}

/**
 * This function checks if a specific property (key) is present in all objects within an array.
 *
 * @param {string} key - The property to check for in the objects.
 * @param {Array} arrayOfObjects - The array of objects to check.
 * @returns {boolean} - Returns true if the property is present in all objects, false otherwise.
 */
export function isPropertyPresent(key, arrayOfObjects) {
  for (let object of arrayOfObjects) {
    if (!(key in object)) {
      return false;
    }
  }
  return true;
}
/**
 * This function updates a specific object in the parameters array.
 * It iterates over the parameters array and when it finds an object with the same name as the input object,
 * it replaces the object in the array with the input object.
 *
 * @param {Object} items - The object to be updated.
 * @param {Array} parameters - The array of objects to be searched.
 * @return {Array} The updated parameters array.
 */
export function updateParameters(items, parameters) {
  // Iterate over each object in the parameters array
  for (let i = 0; i < parameters.length; i++) {
    // If the name of the current object matches the name of the input object
    if (parameters[i].name === items.name) {
      // Replace the current object with the input object
      parameters[i] = items;
    }
  }
  // Return the updated parameters array
  return parameters;
}

/**
 * This function searches for an object with a specific name within an array of parameters.
 * If the object is found and does not have a 'property' property, it adds one with a value of "".
 *
 * @param {string} name - The name of the parameter to search for.
 * @param {Array} parameters - The array of parameters to search within.
 * @return {Object} The found parameter with the 'property' property added if it was not present.
 */
export function retrievesParameters(name, parameters) {
  // Iterate over each parameter in the parameters array
  for (let param of parameters) {
    // Check if the name of the parameter matches the searched name
    if (param.name == name) {
      // If the 'property' property is not present, add it with a value of ""
      if (!isPropertyPresent("property", [param])) {
        param.property = "";
      }
      // Return the parameter
      return param;
    }
  }
}

/**
 * This function takes an input value and determines its type.
 * If the type of the value is a string or a number, it returns 'text'.
 * If the type of the value is an object, it checks if it's an array.
 * For any other types, it returns real type.
 *
 * @param {any} value - The value to determine the type of.
 * @returns {string} - The type of the value: 'text', 'array', 'object', or type.
 */
export function recognizeType(value) {
  let type = typeof value;
  if (type === "string" || type === "number") {
    return "text";
  } else if (type === "object") {
    if (Array.isArray(value)) {
      return "array";
    } else {
      return "object";
    }
  } else {
    return type;
  }
}

/**
 * This function retrieves all unique keys from an array of objects.
 *
 * @param {Array} input - The array of objects to retrieve keys from.
 * @returns {Array} An array of unique keys present in the input objects.
 * For nested objects, keys are represented as 'key.subKey'.
 */
export function getUniqueKeys(input) {
  // Initialize a new Set to store unique keys
  let keys = new Set();
  // If input is a single object, convert it to an array containing that object
  if (!Array.isArray(input)) {
    input = [input];
  }
  // Iterate over each object in the array
  input.forEach((obj) => {
    // Get all keys of the current object and add them to the Set
    Object.keys(obj).forEach((key) => {
      keys.add(key);

      // If the value of the current key is an object (and not null)
      if (typeof obj[key] === "object" && obj[key] !== null) {
        // Get all keys of the sub-object and add them to the Set
        // Sub-object keys are represented as 'key.subKey'
        Object.keys(obj[key]).forEach((subKey) => {
          keys.add(`${key}.${subKey}`);
        });
      }
    });
  });

  // Convert the Set of keys into an array and return it
  return Array.from(keys);
}
